//export type CaseTypeType = 'ME' | 'CA' | 'BA' | 'PA' | 'PL'
export type isolocaldate = string

export enum CaseTypeEnum {
  ME = 'ME',
  CA = 'CA',
  BA = 'BA',
  PA = 'PA',
  PL = 'PL',
  TI = 'TI',
}
export type CaseTypeType = keyof typeof CaseTypeEnum

export type CaseReportId = string

export type CaseReportParamsType = {
  trip?: TravelDataType
  claimant: ClaimantDataType
  caseCommon?: CaseCommonDataType
  caseME?: CaseMEParamsType
  caseCA?: CaseCAParamsType
  caseBA?: CaseBAParamsType
  casePL?: CasePLParamsType
  casePA?: CasePAParamsType
  caseTI?: CaseTIParamsType
  other?: OtherDataType
}

/** Data type of POST /case-report */
export type InsuranceCaseType = InsuranceCaseChangeType & {
  webUserId?: string
  claimant?: ClaimantDataType
}

export type PostCaseReportRespType = {
  token: string
  id: CaseReportId
}

export type NextCaseReportRequestParamsType = {
  policyNumber: string
}

/** Data type of PUT /case-report/{id}/insurance */
export type InsuranceCaseChangeType = {
  caseType: CaseTypeType
  insuranceData: InsuranceDataType
}

export type CaseReportRespType = {
  caseType: CaseTypeType
  webUserId?: string
  insurance?: InsuranceDataType
  trip?: TravelDataType
  claimant: ClaimantDataType
  caseCommon?: CaseCommonDataType
  caseME?: CaseMERespType
  caseCA?: CaseCARespType
  caseBA?: CaseBARespType
  casePL?: CasePLRespType
  casePA?: CasePARespType
  caseTI?: CaseTIRespType
  other?: OtherDataType
  attachmentList?: AttachmentListRespType[]
  notifNum?: string
  createdAt?: Date
}

export const AttachmentType = {
  INVOICE: 'INVOICE',
  MEDIC: 'MEDIC',
  MEDICABROAD: 'MEDICABROAD',
  MEDICPL: 'MEDICPL',
  OTHER: 'OTHER',
  PASS: 'PASS',
  PHOTO: 'PHOTO',
  PIR: 'PIR',
  POLICY: 'POLICY',
  RECEIPT: 'RECEIPT',
  REPORT: 'REPORT',
  SUPPORT: 'SUPPORT',
  TESTIMONY: 'TESTIMONY',
  TICKET: 'TICKET',
  CASEREPORT: 'CASEREPORT',
} as const

export type AttachmentType = typeof AttachmentType[keyof typeof AttachmentType]

// v APIARY je pospsan typ zde pojmenovany AttachmentListRespType
export type AttachmentListParamsType = {
  attachmentType: string | AttachmentType
  attachments?: AttachmentParamsType[] // AttachmentRespType má být dle APIARY
}

export type AttachmentListRespType = {
  attachmentType: string | AttachmentType | null
  attachments?: AttachmentRespType[]
}

export type AttachmentRespType = {
  attachmentId: string
  fileName: string
}

// parametry POST attachment
/**
 *
 * @export
 * @enum {string}
 */
export const PartType = {
  CASE: 'CASE',
  CASEBA: 'CASEBA',
  CASEPL: 'CASEPL',
  CASEPA: 'CASEPA',
  CASETI: 'CASETI',
  COSTOP: 'COSTOP',
  COSTIP: 'COSTIP',
  COSTOTH: 'COSTOTH',
  COSTLE: 'COSTLE',
  ITEMBA: 'ITEMBA',
  ITEMPL: 'ITEMPL',
  PERSON: 'PERSON',
  WITNESS: 'WITNESS',
} as const

export type PartType = typeof PartType[keyof typeof PartType]

export type AttachmentParamsType = {
  part: PartType
  type: AttachmentType
  itemOrderNum: number // integer - positive or zero
  fileName: string // nazev souboru, jak jej chce pojmenovat frontend
}

export type InsuranceDataType = {
  policyNumber?: string
  nonResident?: boolean
  firstName?: string | null
  lastName?: string | null
  birthDate?: string | null
}

export type TravelType = 'I' | 'T' | 'B'

export type TravelDataType = {
  travelType?: TravelType
  touroperator?: string
  companyName?: string
  departureDate?: string | null
  returnDate?: string | null
}

// Neni soucast datoveho modelu API. pouziva se uvnitr Context (col.tsx)
// Typ reprezentuje kombinaci TravelData a attachmentu,
// ktere reprezentuji CaseReportResp.trip a CaseReportResp.attachmentList
export type TravelContextType = TravelDataType & {
  attachmentList?: AttachmentListRespType[]
}

export type ClaimantType = 'PERSON' | 'COMPANY'

export type ClaimantPersonDataType = {
  firstName?: string | null
  lastName?: string | null
  idnum?: string | null
}

export type ClaimantCompanyDataType = {
  companyName?: string | null
  nip?: string | null
}

export type ClaimantDataType = {
  claimantType?: ClaimantType
  isInsuredPerson?: boolean
  nonResident?: boolean
  person?: ClaimantPersonDataType
  company?: ClaimantCompanyDataType
  address?: AddressType
  phone?: string
  email?: string
}

export type AddressType = {
  street?: string
  town?: string
  country?: string
  postCode?: string
}

export type CaseCommonDataType = {
  occurredOn?: string | null
  description?: string
  destination?: string | null
  resort?: string
  place?: string
  assistance?: boolean | null
  assistanceDesc?: string
}

export type CaseMEDataType = {
  transport?: boolean | null
  transportType?: string
  treatmentTypes?: TreatmentType[] // TreatmentTypeType
  hospitDateFrom?: string | null
  hospitDateTo?: string | null
}

export type CaseMEParamsType = CaseMEDataType & {
  treatmentExpenses?: TreatmentExpensesMapParamsType
}

export type CaseMERespType = CaseMEDataType & {
  attachmentList?: AttachmentListRespType[]
  treatmentExpenses?: TreatmentExpensesMapRespType
}

/**
 * DamageType: Type of property damage for COL reports.
 */
export enum DamageTypeEnum {
  LOSS = 'LOSS',
  DELAY = 'DELAY',
  THEFT = 'THEFT',
  PROPERTY = 'PROPERTY',
  BODY = 'BODY',
  INJURY = 'INJURY',
  DEATH = 'DEATH',
}

export type DamageType = keyof typeof DamageTypeEnum

export type CaseBADataType = {
  damageType?: DamageType
  baggageDateTime?: string | null
  theftReported?: boolean | null
}

export type CaseBAParamsType = CaseBADataType & {
  items?: ItemParamsType[]
}

export type CaseBARespType = CaseBADataType & {
  items?: ItemRespType[]
  attachmentList?: AttachmentListRespType[]
}

type ItemDataType = {
  itemType?: string | null
  orderNum: number
  description?: string | null
  amount?: number | null
  currency?: string | null
  purchaseDate?: string | null
  date?: Date | null
  ownerName?: string | null
  ownerContact?: string | null
  ownerRelative?: boolean | null
}

type ItemParamsType = ItemDataType

export type ItemRespType = ItemDataType & {
  attachmentList: AttachmentListRespType[]
}

export type CasePLDataType = {
  damageType?: DamageType
  witnessExists?: boolean | null
  legalExpensesExists?: boolean | null
}

export type CasePLParamsType = CasePLDataType & {
  witnesses?: PersonParamsType[]
  persons?: PersonParamsType[]
  legalExpenses?: TreatmentExpensesParamsType
  items?: ItemParamsType[]
}

export type CasePLRespType = CasePLDataType & {
  witnesses?: PersonRespType[]
  persons?: PersonRespType[]
  legalExpenses?: TreatmentExpensesRespType
  items?: ItemRespType[]
}

export type CasePADataType = {
  damageType?: DamageType
  claimReported?: boolean
  claimNumber?: string
  treatedAbroad?: boolean
  treatmentFacility?: string
  homeDoctorName?: string
  homeDoctorContact?: string
}

export type CasePAParamsType = CasePADataType

export type CasePARespType = CasePADataType & {
  attachmentList?: AttachmentListRespType[]
}

export type CaseCADataType = {
  reservationDate?: string | null
  firstPaymentDate?: string | null
  reason?: string
  insuredRelation?: string
  // typ ma byt PersonDataType a ne InsuredPersonType -> TODO refaktorovat
  insuredPersons?: InsuredPersonType[]
  insuredPersonExists?: boolean
  priceCur?: number | null
  refundCur?: number | null
  currency?: string | null
}

export type CaseCAParamsType = CaseCADataType

export type CaseCARespType = CaseCADataType & {
  attachmentList?: AttachmentListRespType[]
}

export type CaseTIDataType = {
  eventName?: string | null
  reason?: string | null
  insuredRelation?: string
  priceCur?: number | null
  currency?: string | null
}

export type CaseTIParamsType = CaseTIDataType

export type CaseTIRespType = CaseTIDataType & {
  attachmentList?: AttachmentListRespType[]
}
// pouziva se zrejme z puvodniho kodu; neni soucast modelu
export type InsuredPersonType = {
  orderNum: number
  firstName: string
  lastName: string
  insuredRelation: string
  birthDate: string | null
  // email a phone - zde nejsou
}

export type PersonDataType = {
  orderNum: number
  firstName?: string
  lastName?: string
  insuredRelation?: string
  birthDate?: string | null
  phone?: string
  email?: string
}

export type PersonParamsType = PersonDataType

export type PersonRespType = PersonDataType & {
  attachmentList: AttachmentListRespType[]
}

export type TreatmentExpensesParamsType = {
  treatmentType: TreatmentType | string
  costs?: CostParamsType[] | null
}

export type TreatmentExpensesRespType = {
  treatmentType: TreatmentType | string
  costs: CostRespType[] | null
}

export type TreatmentExpensesMapParamsType = {
  IP?: TreatmentExpensesParamsType
  OP?: TreatmentExpensesParamsType
  OTH?: TreatmentExpensesParamsType
  LE?: TreatmentExpensesParamsType
}

export type TreatmentExpensesMapRespType = {
  IP?: TreatmentExpensesRespType
  OP?: TreatmentExpensesRespType
  OTH?: TreatmentExpensesRespType
}

export type TreatmentType = 'OP' | 'IP' | 'OTH' | 'LE'

export type CostDataType = {
  orderNum: number
  expenseType?: string
  costDate?: string | null
  amount?: number | null
  currency?: string | null
  paidDirectly?: boolean
}

export type CostParamsType = CostDataType

// dle definice v apiary
export type CostRespType = CostDataType & {
  attachmentList?: AttachmentListRespType[]
}

export type OtherDataType = {
  otherInsurance?: boolean
  otherInsurer?: null | string
  bankCard?: boolean
  cardIssuer?: null | string
  bankAccount?: {
    type?: string | AccountType
    accountNumber?: null | string
    iban?: null | string
    bankSwift?: null | string
    accountHolder?: null | string
  }
}

export type AccountType = 'D' | 'F'

export type UserProfileDataType = {
  claimant: ClaimantDataType
  birthDate: isolocaldate | null
}
